<template>
	<div class="slider">
		<div class="swiper-container" ref="slider">
			<div class="swiper-wrapper wrapper">
				<div
					v-for="(slide, index) in slides"
					:key="index"
					class="swiper-slide slide"
					:style="{
						backgroundImage: `url(${slide.backgroundImage})`
					}"
				>
					<div
						class="small-background-image"
						:style="{
							backgroundImage: `url(${slide.smallBackgroundImage})`
						}"
						v-if="slide.smallBackgroundImage"
					/>
					<video
						v-if="slide.backgroundVideo"
						class="video"
						muted
						autoplay
						loop
						playsinline
					>
						<source :src="slide.backgroundVideo" type="video/mp4" />
					</video>
				</div>
			</div>
			<div class="content">
				<div class="container">
					<h2 class="headline" v-if="headline">
						{{ headline }}
					</h2>
					<div class="box">
						<h3 class="box__headline">{{ currentSlide.headline }}</h3>
						<p class="box__body" v-html="currentSlide.body"></p>

						<a
							v-if="currentSlide.link"
							:href="currentSlide.link.url"
							class="t-link box__link"
							>{{ currentSlide.link.label }}</a
						>

						<div class="meta" v-show="slides.length > 1">
							<div class="meter">
								<div class="progress" ref="progressBar"></div>
							</div>
							<div class="page">
								{{ currentSlideIndex + 1 }}/{{ slides.length }}
							</div>
							<div class="navigation">
								<div class="navigation__button navigation__button--prev">
									<i class="fal fa-chevron-left"></i>
								</div>
								<div class="navigation__button navigation__button--next">
									<i class="fal fa-chevron-right"></i>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Swiper from "swiper/dist/js/swiper.js";;

export default {
	name: "Slider",
	selector: "#vue-slider",
	props: {
		slides: {
			type: Array,
			required: true
		},
		headline: {
			type: String,
			required: false
		}
	},
	data() {
		return {
			currentSlideIndex: 0,
			timestamp: Date.now()
		};
	},
	computed: {
		currentSlide() {
			return this.slides[this.currentSlideIndex];
		}
	},
	mounted() {
		this.slider = new Swiper(this.$refs.slider, {
			effect: "fade",
			loop: true,
			autoplay: {
				delay: 5000,
				disableOnInteraction: false
			},
			allowTouchMove: false,
			navigation: {
				prevEl: ".navigation__button--prev",
				nextEl: ".navigation__button--next",
				disabledClass: "navigation__button--disabled"
			}
		});

		this.slider.on("slideChange", () => {
			this.currentSlideIndex = this.slider.realIndex;
			this.timestamp = Date.now();
		});

		this.slider.on("slideChangeTransitionStart", () => {
			// Swiper will call slideChange before slideChangeTransitionStart, so index has been updated
			const nextSlideIndex = this.currentSlideIndex;
			const slideEl = this.$el.querySelectorAll(".swiper-slide")[
				this.currentSlideIndex
			];
			const video = slideEl.querySelector("video");
			if (video) video.currentTime = 0;
		});

		const loop = () => {
			const msElapsed = Date.now() - this.timestamp;
			const percentage = msElapsed / 5000;
			this.$refs.progressBar.style.transform = `scaleX(${percentage})`;
			window.requestAnimationFrame(loop);
		};

		loop();
	}
};
</script>

<style lang="scss" scoped>
.slide {
	background-size: cover;
	background-position: center;
	height: 798px;

	@include media("<=tablet") {
		height: 526px;
	}

	@include media("<=phone") {
		height: 300px;
	}
}

.small-background-image {
	background-position: center;
	background-size: cover;
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	z-index: -2;
	display: none;

	@include media("<=tablet") {
		display: block;
	}
}

.video {
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	z-index: -1;
	min-width: 100%;
	min-height: 100%;
}

.wrapper {
	/* position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  z-index: -1; */
}

.content {
	position: absolute;
	bottom: 0;
	left: 0;
	z-index: 1;
	width: 100%;
	&__container {
		@include inner-container();
		padding-bottom: 128px;
	}
	@include media("<=tablet") {
	}
}

.swiper-container {
	@include media("<=tablet") {
		margin-bottom: 250px;
		overflow: visible;
	}

	@include media("<=phone") {
		margin-bottom: 328px;
	}
}

.container {
	@include inner-container;
	padding-top: 96px;
	padding-bottom: 96px;
	@include media("<=460px") {
		padding-left: 24px;
		padding-right: 24px;
	}
}

.headline {
	margin: 0;
	font-size: 56px;
	color: $white;
	line-height: 56px;
	white-space: pre-line;
	font-family: $font-family;
	overflow: hidden;

	@include media("<=tablet") {
		text-align: center;
		font-size: 48px;
		line-height: 48px;
	}

	@include media("<=phone") {
		font-size: 24px;
		line-height: 26px;
	}
}

.box {
	margin-top: 120px;
	background-color: $white;
	padding: 56px 64px;
	box-shadow: 0 20px 40px 0 rgba(0, 0, 0, 0.06);
	width: 42%;

	@include media("<=desktop") {
		width: 66.67%;
	}

	@include media("<=tablet") {
		width: 100%;
		margin-top: 56px;
		margin-bottom: -250px;
	}

	@include media("<=460px") {
		padding: 24px 32px;
	}

	@include media("<=phone") {
		margin-bottom: -360px;
	}

	&__headline {
		margin: 0;
		font-size: 21px;
		color: $teal;
	}

	&__body {
		margin: 0;
		margin-top: 16px;
		font-size: 14px;
		line-height: 20px;
		white-space: pre-line;
		min-height: 60px;

		@include media("<=tablet") {
			min-height: 140px;
		}
	}

	&__link {
		display: inline-block;
		margin-top: 24px;
	}
}

.meta {
	margin-top: 48px;
	display: flex;
	align-items: center;
	color: black;
	font-size: 14px;
	letter-spacing: 0;
}

.meter {
	width: 130px;
	height: 2px;
	background: rgba($black, 0.1);
	overflow: hidden;
}

.progress {
	background: $teal-light;
	height: 2px;
	transform-origin: left;
}

.page {
	margin-left: 16px;
	font-weight: 700;
}

.navigation {
	display: flex;
	margin-left: auto;
}

.navigation__button {
	cursor: pointer;
	&--next {
		margin-left: 16px;
	}

	&--disabled {
		opacity: 0.2;
	}
}
</style>
